import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Divider,
  Avatar,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import Breadcrumbs from '../components/Breadcrumbs';
import './BlogDetail.css';

// Mock data for blog articles
const mockArticles = [
  {
    id: 1,
    title: "Les tendances du sport en 2024 : vers une pratique plus inclusive",
    excerpt: "Découvrez comment le monde du sport évolue pour devenir plus accessible et inclusif pour tous les pratiquants.",
    content: "Le sport connaît une révolution silencieuse mais profonde. En 2024, nous assistons à une transformation majeure de la façon dont nous concevons et pratiquons les activités sportives.\n\n## Une nouvelle approche de l'inclusion\n\nL'inclusion dans le sport ne se limite plus aux adaptations pour les personnes en situation de handicap. Elle englobe désormais une vision plus large qui prend en compte la diversité des corps, des âges, des origines sociales et culturelles.\n\n### Les initiatives qui marquent 2024\n\n- **Sport adapté** : Développement de programmes spécifiques pour chaque profil\n- **Accessibilité financière** : Mise en place de tarifs solidaires\n- **Diversité des pratiques** : Émergence de nouvelles disciplines hybrides\n- **Formation des encadrants** : Sensibilisation à l'inclusion\n\n## L'impact de la technologie\n\nLes nouvelles technologies jouent un rôle crucial dans cette démocratisation du sport. Des applications mobiles aux équipements connectés, tout concourt à rendre le sport plus accessible.\n\n### Exemples concrets\n\n1. **Applications de coaching personnalisé** adaptées aux besoins spécifiques\n2. **Équipements sportifs modulables** pour différents niveaux\n3. **Plateformes de mise en relation** entre pratiquants\n\n## Vers un avenir plus inclusif\n\nCette tendance vers plus d'inclusion n'est pas qu'une mode passagère. Elle répond à un besoin réel de notre société de créer des espaces où chacun peut s'épanouir selon ses capacités et ses envies.\n\nL'objectif est clair : faire du sport un véritable outil d'épanouissement personnel et de cohésion sociale, accessible à tous sans exception.",
    author: "Marie Dubois",
    publishDate: "2024-01-15",
    category: "Tendances",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800",
    tags: ["sport", "inclusion", "tendances", "2024"],
    readTime: "5 min"
  },
  {
    id: 2,
    title: "Comment choisir le bon coach sportif pour vos objectifs",
    excerpt: "Guide complet pour sélectionner le professionnel qui vous accompagnera vers la réussite de vos projets sportifs.",
    content: "Choisir un coach sportif est une décision importante qui peut déterminer le succès de votre parcours fitness. Voici un guide complet pour vous aider à faire le bon choix.\n\n## Définir vos objectifs\n\nAvant de chercher un coach, il est essentiel de clarifier vos objectifs :\n\n- **Perte de poids** : Recherchez un coach spécialisé en nutrition et cardio\n- **Prise de masse** : Privilégiez un expert en musculation\n- **Préparation sportive** : Optez pour un coach spécialisé dans votre discipline\n- **Remise en forme** : Un coach généraliste peut convenir\n\n## Les critères de sélection\n\n### Qualifications et certifications\n\n- Diplômes reconnus (BPJEPS, DEUST, Licence STAPS)\n- Certifications spécialisées\n- Formation continue\n- Assurance professionnelle\n\n### Expérience et spécialisation\n\n- Années d'expérience\n- Spécialisation dans votre domaine\n- Témoignages et références\n- Résultats obtenus avec d'autres clients\n\n### Approche pédagogique\n\n- Méthode d'enseignement\n- Capacité d'adaptation\n- Suivi personnalisé\n- Communication et motivation\n\n## Questions à poser lors du premier entretien\n\n1. Quelle est votre approche pour atteindre mes objectifs ?\n2. Comment adaptez-vous vos séances selon les progrès ?\n3. Proposez-vous un suivi nutritionnel ?\n4. Quelle est votre disponibilité ?\n5. Quels sont vos tarifs et modalités de paiement ?\n\n## Les signaux d'alarme à éviter\n\n- Promesses irréalistes\n- Absence de diplômes vérifiables\n- Approche trop rigide\n- Manque de suivi personnalisé\n- Tarifs anormalement bas ou élevés\n\n## Conclusion\n\nLe bon coach sportif est celui qui comprend vos besoins, s'adapte à votre rythme et vous accompagne avec bienveillance vers vos objectifs. Prenez le temps de bien choisir, c'est un investissement dans votre santé et votre bien-être.",
    author: "Thomas Martin",
    publishDate: "2024-01-10",
    category: "Conseils",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800",
    tags: ["coaching", "conseils", "objectifs"],
    readTime: "7 min"
  },
  {
    id: 3,
    title: "L'importance de la préparation mentale dans le sport",
    excerpt: "La performance sportive ne dépend pas uniquement du physique. Explorez l'impact crucial de la préparation mentale.",
    content: "La préparation mentale est souvent négligée dans l'entraînement sportif, pourtant elle représente un facteur déterminant de la performance.\n\n## Qu'est-ce que la préparation mentale ?\n\nLa préparation mentale englobe l'ensemble des techniques visant à optimiser les ressources psychologiques de l'athlète pour améliorer ses performances et son bien-être.\n\n### Les composantes principales\n\n- **Concentration** : Capacité à focaliser son attention\n- **Confiance en soi** : Croyance en ses capacités\n- **Gestion du stress** : Contrôle de l'anxiété et de la pression\n- **Motivation** : Maintien de l'engagement sur le long terme\n- **Visualisation** : Répétition mentale des gestes techniques\n\n## Les bénéfices de la préparation mentale\n\n### Sur la performance\n\n- Amélioration de la régularité\n- Meilleure gestion des moments clés\n- Optimisation de l'apprentissage technique\n- Récupération plus efficace\n\n### Sur le bien-être\n\n- Réduction du stress et de l'anxiété\n- Amélioration de la confiance en soi\n- Plaisir de la pratique préservé\n- Prévention du burn-out\n\n## Techniques de préparation mentale\n\n### La relaxation\n\n- Respiration contrôlée\n- Relaxation progressive\n- Méditation de pleine conscience\n\n### La visualisation\n\n- Répétition mentale des gestes\n- Visualisation des situations de compétition\n- Imagerie multisensorielle\n\n### Le dialogue interne\n\n- Formulation d'objectifs positifs\n- Restructuration cognitive\n- Auto-instructions efficaces\n\n### La fixation d'objectifs\n\n- Objectifs SMART (Spécifiques, Mesurables, Atteignables, Réalistes, Temporels)\n- Objectifs de processus vs objectifs de résultat\n- Planification progressive\n\n## Mise en pratique\n\n### Pour les débutants\n\n1. Commencer par 5-10 minutes de relaxation quotidienne\n2. Pratiquer la visualisation avant l'entraînement\n3. Tenir un journal de ses sensations et émotions\n\n### Pour les sportifs confirmés\n\n1. Intégrer la préparation mentale dans le plan d'entraînement\n2. Travailler avec un préparateur mental\n3. Adapter les techniques selon les spécificités de sa discipline\n\n## Conclusion\n\nLa préparation mentale n'est plus un luxe réservé aux athlètes de haut niveau. Elle constitue un pilier essentiel de la performance sportive, au même titre que la préparation physique et technique. Investir dans son mental, c'est se donner toutes les chances de réussir et de prendre du plaisir dans sa pratique sportive.",
    author: "Sophie Laurent",
    publishDate: "2024-01-05",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800",
    tags: ["mental", "performance", "psychologie"],
    readTime: "6 min"
  }
];

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Find the article by ID
  const article = mockArticles.find(article => article.id === parseInt(id));
  
  if (!article) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Article non trouvé
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          L'article que vous recherchez n'existe pas ou a été supprimé.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
        >
          Retour aux actualités
        </Button>
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Actualités', path: '/blog' },
    { label: article.title, path: '/blog/' + article.id }
  ];

  // Format content with line breaks
  const formatContent = (content) => {
    return content.split('\n').map((paragraph, index) => {
      if (paragraph.trim() === '') {
        return <br key={index} />;
      }
      
      // Handle headers
      if (paragraph.startsWith('## ')) {
        return (
          <Typography key={index} variant="h4" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            {paragraph.replace('## ', '')}
          </Typography>
        );
      }
      
      if (paragraph.startsWith('### ')) {
        return (
          <Typography key={index} variant="h5" component="h3" gutterBottom sx={{ mt: 3, mb: 1.5 }}>
            {paragraph.replace('### ', '')}
          </Typography>
        );
      }
      
      // Handle numbered lists
      if (/^\d+\./.test(paragraph.trim())) {
        return (
          <Typography key={index} variant="body1" component="li" sx={{ ml: 2, mb: 1 }}>
            {paragraph.trim()}
          </Typography>
        );
      }
      
      // Handle bullet points
      if (paragraph.trim().startsWith('- ')) {
        return (
          <Typography key={index} variant="body1" component="li" sx={{ ml: 2, mb: 1 }}>
            {paragraph.replace('- ', '')}
          </Typography>
        );
      }
      
      // Regular paragraphs
      return (
        <Typography key={index} variant="body1" paragraph>
          {paragraph}
        </Typography>
      );
    });
  };

  return (
    <>
      <Helmet>
        <title>{article.title} | Mission Sport</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.image} />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs items={breadcrumbItems} />
        
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blog')}
            sx={{ mb: 2 }}
          >
            Retour aux actualités
          </Button>
        </Box>

        <Paper elevation={0} sx={{ p: 4 }}>
          {/* Article Header */}
          <Box sx={{ mb: 4 }}>
            <Chip 
              label={article.category} 
              color="primary" 
              sx={{ mb: 2 }}
            />
            
            <Typography variant="h3" component="h1" gutterBottom>
              {article.title}
            </Typography>
            
            <Typography variant="h6" color="text.secondary" paragraph>
              {article.excerpt}
            </Typography>
            
            {/* Article Meta */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {article.author}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {new Date(article.publishDate).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {article.readTime}
                </Typography>
              </Box>
            </Box>
            
            {/* Article Image */}
            <Box
              component="img"
              src={article.image}
              alt={article.title}
              sx={{
                width: '100%',
                height: 400,
                objectFit: 'cover',
                borderRadius: 2,
                mb: 3
              }}
            />
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article Content */}
          <Box sx={{ mb: 4 }}>
            {formatContent(article.content)}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article Tags */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {article.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {/* Back to Blog Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/blog')}
            >
              Retour au blog
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default BlogDetail;